import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Snackbar, Alert,
  CircularProgress, IconButton, Tooltip, Chip, Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { format } from 'date-fns';
import api from '../services/api';

const ManageHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/holidays');
      setHolidays(res.data);
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const handleAdd = async () => {
    if (!newDate) {
      setSnackbar({ open: true, message: 'Please select a date.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/holidays', { date: newDate, description: newDescription.trim() || undefined });
      setSnackbar({ open: true, message: 'Holiday added successfully!', severity: 'success' });
      setNewDate('');
      setNewDescription('');
      fetchHolidays();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to add holiday.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/holidays/${id}`);
      setSnackbar({ open: true, message: 'Holiday removed.', severity: 'success' });
      fetchHolidays();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to remove holiday.', severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'EEEE, dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const isPast = (dateStr) => dateStr < format(new Date(), 'yyyy-MM-dd');

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#052e2b' }}>
        Manage Holidays
      </Typography>

      {/* Add holiday */}
      <Paper sx={{ p: 3, mb: 3, maxWidth: 600 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#052e2b' }}>
          Add Holiday
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Booking will be blocked on all dates marked as holidays.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            label="Date"
            type="date"
            size="small"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            label="Description (optional)"
            size="small"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="e.g. Diwali"
            sx={{ width: 240 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={saving || !newDate}
            sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Holiday list */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#052e2b' }}>
          Holidays ({holidays.length})
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : holidays.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <EventBusyIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No holidays configured.</Typography>
          </Box>
        ) : (
          <Box>
            {holidays.map((h, idx) => (
              <Box key={h.id}>
                {idx > 0 && <Divider />}
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, gap: 1 }}>
                  <EventBusyIcon sx={{ color: isPast(h.holiday_date) ? 'text.disabled' : '#d32f2f', fontSize: 20 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      sx={{ color: isPast(h.holiday_date) ? 'text.disabled' : 'text.primary' }}
                    >
                      {formatDate(h.holiday_date)}
                    </Typography>
                    {h.description && (
                      <Typography variant="body2" color="text.secondary">
                        {h.description}
                      </Typography>
                    )}
                  </Box>
                  {isPast(h.holiday_date) && (
                    <Chip label="Past" size="small" variant="outlined" />
                  )}
                  <Tooltip title="Remove holiday">
                    <IconButton size="small" color="error" onClick={() => handleDelete(h.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageHolidays;
